// resources/js/Components/zone/SmartScheduler.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Paper
} from '@mui/material';

const HOURS = 24;
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const SmartScheduler = ({ schedule, setSchedule }) => {
    const [resolution, setResolution] = useState(30); // 15, 30, or 60
    const [grid, setGrid] = useState(generateEmptyGrid(30));
    const gridRef = useRef(grid);
    const isDragging = useRef(false);
    const dragMode = useRef(null);

    useEffect(() => {
        const parsed = parseScheduleToGrid(schedule, resolution);
        gridRef.current = parsed;
        setGrid(parsed);
    }, [schedule, resolution]);

    const handleResolutionChange = (_, value) => {
        if (value !== null) setResolution(value);
    };

    const handleMouseDown = (dayIndex, slotIndex) => {
        isDragging.current = true;
        const currentlySelected = grid[dayIndex][slotIndex];
        dragMode.current = !currentlySelected;
        toggleGridCell(dayIndex, slotIndex, dragMode.current);
    };

    const handleMouseOver = (dayIndex, slotIndex) => {
        if (isDragging.current && dragMode.current !== null) {
            toggleGridCell(dayIndex, slotIndex, dragMode.current);
        }
    };

    const handleMouseUp = () => {
        if (!isDragging.current) return;
        isDragging.current = false;
        dragMode.current = null;
        setSchedule(convertGridToSchedule(gridRef.current, resolution));
    };

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [resolution, setSchedule]);

    const toggleGridCell = (dayIndex, slotIndex, newState) => {
        const updated = gridRef.current.map((row) => [...row]);
        updated[dayIndex][slotIndex] = newState;
        gridRef.current = updated;
        setGrid(updated);
    };

    const handleDayLabelClick = (dayIndex) => {
        const updated = gridRef.current.map((row) => [...row]);
        updated[dayIndex] = Array(updated[dayIndex].length).fill(!updated[dayIndex].some(Boolean));
        gridRef.current = updated;
        setGrid(updated);
        setSchedule(convertGridToSchedule(updated, resolution));
    };

    const slotsPerDay = HOURS * (60 / resolution);

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">Interactive Grid</Typography>
                <ToggleButtonGroup
                    value={resolution}
                    exclusive
                    onChange={handleResolutionChange}
                    size="small"
                >
                    <ToggleButton value={15}>15 min</ToggleButton>
                    <ToggleButton value={30}>30 min</ToggleButton>
                    <ToggleButton value={60}>60 min</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box
                sx={{
                    overflowX: 'auto',
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    p: 1,
                    userSelect: 'none'
                }}
                onMouseUp={handleMouseUp}
            >
                {/* Time Labels Row */}
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography
                        variant="body2"
                        sx={{ width: 90,minWidth: 90}}
                    >&nbsp;</Typography>
                    <Box display="flex" flexWrap="nowrap" gap={0.2*(60/resolution)}>
                        {Array.from({ length: HOURS }).map((_, hour) => (
                            <Box
                                key={hour}
                                sx={{
                                    width: 40,
                                    minWidth: 40,
                                    textAlign: 'center',
                                    fontSize: '0.75rem',
                                    borderLeft: '1px solid #ccc',
                                }}
                            >
                                {String(hour).padStart(2, '0')}:00
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Grid Rows by Day */}
                {grid.map((dayRow, dayIndex) => (
                    <Box key={dayIndex} display="flex" alignItems="center" mb={1}>
                        <Typography
                            variant="body2"
                            sx={{ width: 90, minWidth:90, cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => handleDayLabelClick(dayIndex)}
                        >
                            {DAY_LABELS[dayIndex]}
                        </Typography>
                        <Box display="flex" flexWrap="nowrap" gap={0.2}>
                            {dayRow.map((cell, slotIndex) => (
                                <Paper
                                    key={slotIndex}
                                    onMouseDown={() => handleMouseDown(dayIndex, slotIndex)}
                                    onMouseOver={() => handleMouseOver(dayIndex, slotIndex)}
                                    sx={{
                                        width: 40/(60/resolution),
                                        minWidth: 40/(60/resolution),
                                        height: 20,
                                        bgcolor: cell ? 'success.main' : 'grey.200',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.1s'
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default SmartScheduler;

function generateEmptyGrid(resolution) {
    const slots = HOURS * (60 / resolution);
    return Array.from({ length: 7 }, () => Array(slots).fill(false));
}

function parseScheduleToGrid(schedule, resolution) {
    const slots = HOURS * (60 / resolution);
    const grid = generateEmptyGrid(resolution);

    for (const block of schedule) {
        const { start, stop, days } = block;
        const startMin = timeToMinutes(start);
        let stopMin = timeToMinutes(stop);
        if (stopMin == 1439) stopMin = 1440; // Handle 24:00 as 1440 minutes
        const startSlot = Math.floor(startMin / resolution);
        const stopSlot = Math.floor(stopMin / resolution);


        (days || []).forEach((day) => {
            if (!grid[day]) return;
            const fill = (targetDay, from, to) => {
                for (let index = from; index < to; index++) grid[targetDay][index] = true;
            };
            if (start.slice(0, 5) === stop.slice(0, 5)) {
                fill(day, 0, slots);
            } else if (stopMin > startMin) {
                fill(day, startSlot, stopSlot);
            } else {
                fill(day, startSlot, slots);
                fill((day + 1) % 7, 0, stopSlot);
            }
        });
    }
    return grid;
}
function convertGridToSchedule(grid, resolution) {
    const map = new Map();

    grid.forEach((row, day) => {
        let start = null;
        for (let i = 0; i <= row.length; i++) {
            if (row[i]) {
                if (start === null) start = i;
            } else if (start !== null) {
                const startTime = minutesToTime(start * resolution);
                const stopTime = minutesToTime(i * resolution);
                const key = `${startTime}-${stopTime}`;
                if (!map.has(key)) {
                    map.set(key, []);
                }
                map.get(key).push(day);
                start = null;
            }
        }
    });

    const result = [];
    for (const [key, days] of map.entries()) {
        const [start, stop] = key.split("-");
        result.push({ start, stop, days });
    }
    return result;
}

function timeToMinutes(timeStr) {
    const [h, m] = timeStr.split(':').map(Number);
    // if 23:59 or 24:00, return 1439
    if (h === 24) return 1440;
    return h * 60 + m;
}

function minutesToTime(minutes) {
    if (minutes >= 1440) return '23:59:00'; // 24:00 or beyond becomes 23:59:00
    const h = String(Math.floor(minutes / 60)).padStart(2, '0');
    const m = String(minutes % 60).padStart(2, '0');
    return `${h}:${m}:00`;
}
